import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { systemDocumentByEntryId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

/**
 * Load a system-document by Rolodex entry id (develop navigation identity).
 * Works with site-only tags when no pipeline-config is active (legacy HR).
 */
export function useSystemDocumentByEntryId(
  entryId: string | undefined,
  siteId: string
): {
  systemDocumentEntry: SystemDocumentEntry | undefined
  systemDocumentEntryId: string | undefined
  cantileverDocumentId: string | undefined
  filename: string | undefined
  bqDocumentId: string | undefined
  isLoading: boolean
} {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const documentLoadOptions = useMemo(() => {
    if (isPipelineConfigScopeLoading || !entryId) {
      return undefined
    }
    return systemDocumentByEntryId(
      entryId,
      getPipelineConfigScopedTags({ site: siteId })
    )
  }, [
    entryId,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteId,
  ])

  const { data: selectedDocument, isLoading } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      documentLoadOptions
    )

  const systemDocumentEntry = selectedDocument?.[0]
  const systemDocumentEntryId = systemDocumentEntry?.entry.id
  const cantileverDocumentId = systemDocumentEntry
    ? getCantileverDocumentId(systemDocumentEntry)
    : undefined
  const filename = systemDocumentEntry?.entry.data.filename
  const bqDocumentId = systemDocumentEntry
    ? getSystemDocumentBqId(systemDocumentEntry)
    : undefined

  return {
    systemDocumentEntry,
    systemDocumentEntryId,
    cantileverDocumentId,
    filename,
    bqDocumentId,
    isLoading,
  }
}
